import { graphql, PageProps } from 'gatsby';
import { helmetJsonLdProp } from 'react-schemaorg';
import { Collection } from 'schema-dts';

import { BackNavigation } from '@/components/BackNavigation';
import { Layout } from '@/components/Layout';
import { LinkList } from '@/components/LinkList';
import { Seo } from '@/components/Seo';
import { ProductGrid } from '@/components/ProductGrid';

type CategoryPageProps = PageProps<Queries.CategoryTemplateQuery>;

const CategoryPage = ({ data, location }: CategoryPageProps) => {
  if (!data.category) {
    return null;
  }

  const colorScheme =
    data.root?.background === 'light' ? 'color-scheme3' : 'color-scheme2';
  const links = data.category.categories
    ?.filter((child) => !!child?.category)
    .map((child) => {
      const category = child!.category!;

      return {
        id: category.slug!,
        title: category.title!,
        url: `${location.pathname}/${category.slug}`,
      };
    });
  const products = data.category.products
    ?.filter((data) => !!data!.product)
    .map((data) => data!.product!);

  return (
    <Layout data={data}>
      <Seo
        description={data.category.seo?.metaDescription}
        title={data.category.seo?.metaTitle ?? data.category.title!}
        bodyAttributes={{
          class: colorScheme,
        }}
        script={[
          helmetJsonLdProp<Collection>({
            '@context': 'https://schema.org',
            '@type': 'Collection',
            'name': data.category.title!,
          }),
        ]}
      />

      <div className='template-category'>
        {data.root?.slug !== data.category.slug ? (
          <>
            <BackNavigation title={data.category.title!} />
            {data.category.seo?.metaDescription && (
              <h2 className='category-description'>
                {data.category.seo?.metaDescription}
              </h2>
            )}
          </>
        ) : (
          <h1>{data.category.title}</h1>
        )}

        {products && products.length !== 0 && (
          <div className={'products-grid'}>
            <ProductGrid
              colorScheme={colorScheme}
              category={data.root?.slug!}
              products={products}
            />
          </div>
        )}

        {links && links.length !== 0 && <LinkList links={links} />}
      </div>
    </Layout>
  );
};

export default CategoryPage;

export const query = graphql`
  fragment ProductCategory on STRAPI_PRODUCT_CATEGORY {
    background
    slug
    title
  }

  query CategoryTemplate($language: String!, $root: String!, $slug: String!) {
    ...Layout

    category: strapiProductCategory(slug: { eq: $slug }) {
      ...ProductCategory
      categories {
        category {
          ...ProductCategory
        }
      }
      products {
        product {
          ...ProductCard
        }
      }
      seo {
        ...Seo
      }
    }

    root: strapiProductCategory(slug: { eq: $root }) {
      background
      slug
    }
  }
`;
